import { useCallback, memo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Users, Zap, Heart, ShieldCheck } from 'lucide-react';
import {
  formatActivityDate,
  calculateDistance,
  formatDistance,
  getCategoryInfo,
  formatYaarsInAndSpotsLeft,
} from '@utils/helpers';
import { useLocation } from '@context/LocationContext';
import { useTheme } from '@context/ThemeContext';
import { useWishlist } from '@context/WishlistContext';
import { useAuth } from '@context/AuthContext';

const CATEGORY_GRADIENTS = {
  sports:   'from-amber-400 via-orange-400 to-orange-500',
  fitness:  'from-rose-400 via-red-400 to-red-500',
  learning: 'from-emerald-400 via-teal-400 to-teal-500',
  social:   'from-violet-400 via-purple-400 to-purple-500',
  outdoors: 'from-lime-400 via-green-400 to-emerald-500',
  creative: 'from-pink-400 via-fuchsia-400 to-fuchsia-500',
  gaming:   'from-indigo-400 via-blue-500 to-blue-600',
  food:     'from-orange-400 via-amber-400 to-amber-500',
  music:    'from-rose-400 via-pink-400 to-pink-500',
  study:    'from-cyan-400 via-sky-400 to-blue-500',
  travel:   'from-sky-400 via-blue-400 to-indigo-500',
};

const ActivityCard = memo(({ activity, onJoin, wishlistCategoryMuted = false, explorePast = false, onBeforeNavigateToActivity }) => {
  const { location: userLoc } = useLocation();
  const { dark } = useTheme();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const { user } = useAuth();
  const wishlisted = isWishlisted(activity._id);

  const dist = activity.location?.coordinates
    ? calculateDistance(
        userLoc.lat, userLoc.lng,
        activity.location.coordinates[1],
        activity.location.coordinates[0]
      )
    : null;

  const spotsLeft = activity.maxParticipants - (activity.participants?.length || 0);
  const almostFull = spotsLeft <= 2 && spotsLeft > 0;
  const isFull = spotsLeft <= 0;
  const isLive = activity.status === 'live' || activity.isQuick;
  const isJoined = !!(user && activity.participants?.some((p) => String(p._id || p) === String(user._id)));

  const catInfo = getCategoryInfo(activity.category);
  const gradient = CATEGORY_GRADIENTS[activity.category] || CATEGORY_GRADIENTS.sports;

  const onToggleWishlist = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (explorePast) return;
      toggleWishlist(activity);
    },
    [toggleWishlist, activity, explorePast]
  );

  const handleJoin = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isJoined) return;
    onJoin?.(activity._id, {
      verifiedOnly: activity.verifiedOnly === true,
      category: activity.category,
    });
  }, [onJoin, activity._id, activity.verifiedOnly, activity.category, isJoined]);

  const genderBadge = activity.allowedGender === 'male'
    ? { label: 'Boys Only', cls: 'bg-blue-500/75' }
    : activity.allowedGender === 'female'
      ? { label: 'Girls Only', cls: 'bg-pink-500/75' }
      : { label: 'Open to All', cls: 'bg-purple-500/75' };

  const handleActivityLinkClick = useCallback(() => {
    onBeforeNavigateToActivity?.(activity._id);
  }, [onBeforeNavigateToActivity, activity._id]);

  return (
    <Link
      to={`/activity/${activity._id}`}
      onClick={handleActivityLinkClick}
      className={`group block w-full min-w-0 max-w-full rounded-[20px] overflow-hidden transition-all duration-300
        ${explorePast
          ? 'opacity-[0.58] saturate-[0.55] contrast-[0.92] hover:opacity-[0.72] hover:scale-[1.01] active:scale-[0.99]'
          : 'hover:scale-[1.02] active:scale-[0.98]'
        }
        ${dark
          ? 'bg-[#111827]/80 backdrop-blur-xl shadow-lg shadow-black/20 hover:shadow-xl hover:shadow-brand-500/10 ring-1 ring-white/[0.06]'
          : 'bg-white shadow-[0_2px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] ring-1 ring-black/[0.03]'
        }`}
    >
      {/* ── Visual Hero ── */}
      <div className="relative h-[200px] overflow-hidden select-none">
        {activity.image && String(activity.image).trim() !== '' ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
              style={{ backgroundImage: `url(${String(activity.image).trim()})` }}
              role="img"
              aria-hidden
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/35 via-black/10 to-black/40" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-7xl opacity-25 drop-shadow-2xl">
                {activity.emoji || catInfo.emoji}
              </span>
            </div>
          </>
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`}>
            <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-white/15 blur-2xl" />
            <div className="absolute -bottom-6 -left-6 w-28 h-28 rounded-full bg-black/10 blur-xl" />
            <div className="absolute top-6 right-12 w-16 h-16 rounded-full bg-white/10 blur-lg" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-7xl opacity-30 drop-shadow-2xl">
                {activity.emoji || catInfo.emoji}
              </span>
            </div>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/40 via-black/15 to-transparent" />

        {explorePast && (
          <div
            className={`pointer-events-none absolute inset-0 z-[4] ${
              dark ? 'bg-surface-950/45' : 'bg-white/35'
            }`}
            aria-hidden
          />
        )}

        {/* Badges — top-left */}
        <div className={`absolute top-3 left-3 z-[5] flex flex-wrap gap-1.5 max-w-[70%] ${explorePast ? 'opacity-80' : ''}`}>
          {activity.verifiedOnly && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/75 backdrop-blur-sm px-2.5 py-1 text-[10px] font-bold text-white shadow-sm">
              <ShieldCheck size={10} />
              Verified
            </span>
          )}
          <span className={`inline-flex items-center rounded-full backdrop-blur-sm px-2.5 py-1 text-[10px] font-bold text-white shadow-sm ${genderBadge.cls}`}>
            {genderBadge.label}
          </span>
          {isLive && !explorePast && (
            <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-red-500 px-2.5 py-1 text-[10px] font-bold text-white shadow-sm animate-pulse-slow">
              <Zap size={10} className="fill-white" />
              LIVE
            </span>
          )}
        </div>

        {/* Wishlist — top-right */}
        <button
          type="button"
          onClick={onToggleWishlist}
          disabled={explorePast}
          className={`absolute top-3 right-3 z-[5] flex h-9 w-9 items-center justify-center rounded-full transition-all duration-200 active:scale-75
            ${dark ? 'bg-black/40 backdrop-blur-sm' : 'bg-white/70 backdrop-blur-sm shadow-sm'}
            ${wishlisted ? 'scale-110' : 'hover:scale-110'}
            ${explorePast ? 'cursor-not-allowed opacity-35' : ''}`}
          aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        >
          <Heart
            size={16}
            className={`transition-all duration-200 ${
              wishlisted
                ? 'fill-red-500 text-red-500 scale-110'
                : dark ? 'text-white/80' : 'text-surface-700'
            }`}
          />
        </button>

        {/* Category + urgency — bottom on image */}
        <div className="absolute bottom-3 left-3 right-3 z-[5] flex items-center justify-between gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full bg-black/35 backdrop-blur-sm px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm transition-all duration-200 ${
              wishlistCategoryMuted || explorePast ? 'opacity-45 grayscale' : ''
            }`}
          >
            {catInfo.emoji} {catInfo.label}
          </span>
          {almostFull && !explorePast && (
            <span className="inline-flex items-center rounded-full bg-red-500/80 backdrop-blur-sm px-2.5 py-1 text-[10px] font-bold text-white shadow-sm animate-pulse-slow">
              🔥 Almost Full
            </span>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="min-w-0 p-4">
        <h3
          className={`min-w-0 break-words text-[15px] font-semibold leading-snug line-clamp-2 mb-2.5 transition-colors duration-200
            ${explorePast
              ? dark ? 'text-surface-400' : 'text-surface-500'
              : dark
                ? 'text-white group-hover:text-brand-400'
                : 'text-surface-900 group-hover:text-brand-600'
            }`}
        >
          {activity.title}
        </h3>

        <div className="min-w-0 space-y-1.5">
          {((activity.location?.address && activity.location.address !== 'Nearby') || dist != null) && (
            <div className={`flex min-w-0 items-center gap-2 text-[12.5px] ${dark ? 'text-gray-400' : 'text-surface-500'} ${explorePast ? '!text-surface-400 dark:!text-surface-500' : ''}`}>
              <MapPin size={14} className="shrink-0 text-brand-400" />
              <span className="min-w-0 flex-1 truncate">
                {dist != null && formatDistance(dist)}
                {dist != null && activity.location?.address && activity.location.address !== 'Nearby' && ' · '}
                {activity.location?.address && activity.location.address !== 'Nearby' && activity.location.address}
              </span>
            </div>
          )}

          <div className={`flex min-w-0 items-center gap-2 text-[12.5px] ${dark ? 'text-gray-400' : 'text-surface-500'} ${explorePast ? '!text-surface-400 dark:!text-surface-500' : ''}`}>
            <Clock size={14} className="shrink-0 text-violet-400" />
            <span className="min-w-0 flex-1 truncate">{formatActivityDate(activity.dateTime)}</span>
          </div>

          {!explorePast && (
            <div
              className={`flex min-w-0 items-center gap-2 text-[12.5px] font-medium ${
                isFull
                  ? 'text-red-500'
                  : almostFull
                    ? dark ? 'text-amber-400' : 'text-amber-600'
                    : dark ? 'text-gray-400' : 'text-surface-500'
              }`}
            >
              <Users
                size={14}
                className={`shrink-0 ${
                  isFull ? 'text-red-500' : almostFull ? 'text-amber-500' : 'text-emerald-400'
                }`}
              />
              <span className="min-w-0 flex-1 truncate">
                {formatYaarsInAndSpotsLeft(
                  activity.participants?.length || 0,
                  activity.maxParticipants,
                )}
                {!isFull && almostFull ? (
                  <span className={`ml-1 ${dark ? 'text-amber-400' : 'text-amber-600'}`}>
                    · Almost full
                  </span>
                ) : null}
              </span>
            </div>
          )}
        </div>

        {/* CTA */}
        {explorePast ? (
          <div
            className={`mt-4 w-full max-w-full shrink-0 rounded-full border py-2.5 text-center text-[12px] font-bold tracking-wide
              ${dark
                ? 'border-surface-600/60 bg-surface-800/40 text-surface-500'
                : 'border-surface-200 bg-surface-100/80 text-surface-400'
              }`}
          >
            Event Ended
          </div>
        ) : onJoin && !isFull ? (
          <button
            onClick={handleJoin}
            disabled={isJoined}
            className={`mt-4 w-full max-w-full shrink-0 rounded-full py-2.5 text-center text-[13px] font-bold transition-all duration-200
              ${isJoined
                ? 'bg-surface-200 text-surface-500 cursor-not-allowed dark:bg-surface-700 dark:text-surface-300'
                : 'bg-gradient-to-r from-brand-500 to-violet-500 text-white hover:shadow-lg hover:shadow-brand-500/25 hover:brightness-110 active:scale-[0.97]'
              }`}
          >
            {isJoined ? 'Joined' : 'Join the Vibe'}
          </button>
        ) : null}
      </div>
    </Link>
  );
});

ActivityCard.displayName = 'ActivityCard';

export default ActivityCard;
